import { TrackedEvents, trackEvent } from 'analytics';
import { AIModelChoices, CustomUserInfoDTO, getCookie, useCurrentUser, useFeatureFlags } from 'api';
import { addError, logger } from 'monitor';
import { useState } from 'react';
import { isLooseAiModel } from '../../../evidence/types';
import { isReactNativeWebView, postToNativeWebView } from '../../../native';
import { useNetworkStatus } from '../../../network-status';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';

const openNativeCamera = (data: {
  sessionId: number;
  aiModel: string;
  offlineMode: boolean;
  user?: CustomUserInfoDTO;
}): void => {
  if (!isReactNativeWebView()) {
    logger.info('Falling back to web camera');
    throw new Error('React Native WebView not available');
  }
  logger.info('Opening Native Camera', { evidenceSessionId: data.sessionId });

  const csrfToken = getCookie('csrftoken');

  if (!csrfToken || !data.sessionId) {
    addError(new Error('Insufficient data to start native camera session'), {
      csrfToken,
      evidenceSessionId: data.sessionId,
    });
  }

  postToNativeWebView(
    'openCamera',
    {
      aiModel: data.aiModel,
      evidenceSessionId: data.sessionId,
      offlineMode: data.offlineMode,
    },
    {
      user: data.user ?? null,
    }
  );
};

export const useToggleCamera = () => {
  const { data: user } = useCurrentUser();
  const flags = useFeatureFlags();
  const { mode } = useNetworkStatus();
  const isNativeCameraGuidanceDisabled = flags.getFlag('NATIVE_CAMERA_GUIDANCE_DISABLED', false);

  const [hasPermission, setHasPermission] = useState(false);
  const [hasMotionPermission, setHasMotionPermission] = useState(false);
  const [hasOrientationPermission, setHasOrientationPermission] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const { open } = useNotification();

  const requestMotionPermission = async () => {
    // iOS specific permission request for motion
    // @ts-expect-error - DeviceMotionEvent.requestPermission is not in TypeScript types
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        // @ts-expect-error - DeviceMotionEvent.requestPermission is not in TypeScript types
        const permissionState = await DeviceMotionEvent.requestPermission();
        setHasMotionPermission(permissionState === 'granted');

        if (permissionState === 'denied') {
          logger.warn('iOS Device motion access denied');
          trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
            permission: 'motion',
            platform: 'iOS',
          });
          open({
            message: 'Please enable motion access in your device settings',
            type: 'error',
          });
          return false;
        }
        return permissionState === 'granted';
      } catch (error) {
        logger.error('Error requesting motion permission:', error);
        setHasMotionPermission(false);
        return false;
      }
    } else {
      // Non-iOS devices don't need explicit permission
      setHasMotionPermission(true);
      return true;
    }
  };

  const requestOrientationPermission = async () => {
    // iOS specific event that triggers a permission request
    // @ts-expect-error mismatch permission name https://developer.mozilla.org/en-US/docs/Web/API/Permissions
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        // @ts-expect-error mismatch permission name https://developer.mozilla.org/en-US/docs/Web/API/Permissions
        const result = await DeviceOrientationEvent.requestPermission();
        setHasOrientationPermission(result === 'granted');

        if (result === 'denied') {
          logger.warn('iOS Device orientation access denied');
          trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
            permission: 'orientation',
            platform: 'iOS',
          });
          open({
            message: 'Please enable orientation access in your device settings',
            type: 'error',
          });
          return false;
        }
        return result === 'granted';
      } catch (error) {
        logger.error('Error requesting orientation permission:', error);
        setHasOrientationPermission(false);
        return false;
      }
    } else {
      // Non-iOS devices don't need explicit permission
      setHasOrientationPermission(true);
      return true;
    }
  };

  const requestCameraPermission = async () => {
    try {
      const query = await navigator.permissions.query({ name: 'camera' });

      if (query.state === 'denied') {
        logger.warn('Camera access denied');
        trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
          permission: 'camera',
        });
        setHasPermission(false);
        open({
          message: 'Please enable camera access in your device settings',
          type: 'error',
        });
        return false;
      }

      setHasPermission(true);
      return true;
    } catch (error) {
      logger.error('Error requesting camera permission:', error);
      setHasPermission(false);
      return false;
    }
  };

  const openWebCamera = async () => {
    const hasOrientation = await requestOrientationPermission();
    const hasMotion = await requestMotionPermission();
    const hasCamera = await requestCameraPermission();

    if (hasOrientation && hasMotion && hasCamera) {
      setIsCameraOpen(true);
    }
  };

  const toggle = async (data?: { sessionId: number; aiModel: AIModelChoices }) => {
    if (isCameraOpen) {
      setIsCameraOpen(false);
      return;
    }

    if (!data) {
      addError(new Error('No camera data provided'), data);
      await openWebCamera();
      return;
    }

    if (!isNativeCameraGuidanceDisabled && !isLooseAiModel(data.aiModel)) {
      try {
        openNativeCamera({ ...data, offlineMode: mode === 'offline', user });
      } catch {
        await openWebCamera();
      }
    } else {
      await openWebCamera();
    }
  };

  return {
    hasMotionPermission,
    hasOrientationPermission,
    hasPermission,
    isCameraOpen,
    requestCameraPermission,
    requestMotionPermission,
    requestOrientationPermission,
    toggle,
  };
};
